import { AuthenticatedTemplate, useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { UserWithLawFirm } from '@common/types/users';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import LexoLoadingCard from '@/components/LexoLoadingCard';
import { createUser } from '@/services/UserService';
import LawfirmCreation from './LawfirmCreation';

export default function Onboarding() {
    const theme = useTheme();

    const [createdUser, setCreatedUser] = useState<UserWithLawFirm | null>(null);
    const isCreatingUser = useRef(false);

    const account = useAccount();
    const isAuthenticated = useIsAuthenticated();

    const { instance } = useMsal();

    useEffect(() => {
        // Guard against duplicate API calls during React strict mode or re-renders
        if (!isAuthenticated || !account || createdUser || isCreatingUser.current) return;
        isCreatingUser.current = true;

        const createAccountUser = async () => {
            if (
                account.username &&
                account.idTokenClaims?.extension_FirstName &&
                account.idTokenClaims?.extension_LastName
            ) {
                try {
                    const response = await createUser({
                        firstName: account.idTokenClaims?.extension_FirstName as string,
                        middleName:
                            (account.idTokenClaims?.extension_MiddleName as string | undefined) ||
                            null,
                        lastName: account.idTokenClaims?.extension_LastName as string,
                        email: account.username,
                        phoneNumber: null,
                        fax: null,
                        lawyerId: null,
                        roleIds: null,
                    });
                    setCreatedUser(response.data);
                } catch (error) {
                    console.error(error);
                    isCreatingUser.current = false; // Reset on error to allow retry
                }
            } else {
                console.error('Failed to create user');
                instance.logoutRedirect();
            }
        };

        void createAccountUser();
    }, [account, createdUser, isAuthenticated, instance]);

    return (
        <AuthenticatedTemplate>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        padding: '24px',
                        width: '400px',
                        backgroundColor: createdUser
                            ? theme.palette.neutral.white
                            : theme.palette.primary.P_600,
                        color: createdUser
                            ? theme.palette.neutral.black
                            : theme.palette.neutral.white,
                        borderRadius: '4px',
                    }}
                >
                    {createdUser ? (
                        <LawfirmCreation createdUser={createdUser} />
                    ) : (
                        <LexoLoadingCard mainText={'Processing...'} />
                    )}
                </Box>
            </Box>
        </AuthenticatedTemplate>
    );
}
